import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import type { DateFilterType } from "@/utils/interfaces";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";

const DEFAULT_DEBOUNCE_MS = 500;
const PERIOD_STORAGE_KEY = "clinical-insights-period-v1";

function readStoredPeriod(): {
  selectedDate?: dayjs.Dayjs;
  dateFilterType?: DateFilterType;
} {
  try {
    const raw = sessionStorage.getItem(PERIOD_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as {
      selectedDate?: string;
      dateFilterType?: DateFilterType;
    };
    return {
      selectedDate: parsed.selectedDate ? dayjs(parsed.selectedDate) : undefined,
      dateFilterType: parsed.dateFilterType,
    };
  } catch {
    return {};
  }
}

export function useClinicalInsightsFilters(debounceMs = DEFAULT_DEBOUNCE_MS) {
  const storedPeriod = useMemo(() => readStoredPeriod(), []);

  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({
    defaultFilterType: storedPeriod.dateFilterType ?? "month",
    initialSelectedDate: storedPeriod.selectedDate,
  });

  useEffect(() => {
    sessionStorage.setItem(
      PERIOD_STORAGE_KEY,
      JSON.stringify({
        selectedDate: selectedDate.toISOString(),
        dateFilterType,
      })
    );
  }, [selectedDate, dateFilterType]);

  const [appliedDateRange, setAppliedDateRange] =
    useState<ClinicalInsightDateRange>(dateRange);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedDateRange(dateRange);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [dateRange, debounceMs]);

  const { selectedPractitioner, handlePractitionerChange, practitioner } =
    useAdministrativeFilters({});

  const practitionerId = selectedPractitioner || "all";

  const resetPeriod = () => {
    handleDateChange(dayjs());
    handleFilterTypeChange("month");
  };

  const resetAll = () => {
    resetPeriod();
    handlePractitionerChange("");
  };

  const isDefaultPeriod = useMemo(
    () =>
      dateFilterType === "month" && selectedDate.isSame(dayjs(), "month"),
    [dateFilterType, selectedDate]
  );

  const hasActiveFilters = useMemo(
    () => !isDefaultPeriod || practitionerId !== "all",
    [isDefaultPeriod, practitionerId]
  );

  const isApplying = useMemo(() => {
    return (
      dateRange.startDate !== appliedDateRange.startDate ||
      dateRange.endDate !== appliedDateRange.endDate
    );
  }, [dateRange, appliedDateRange]);

  return {
    selectedDate,
    dateFilterType,
    appliedDateRange,
    practitionerId,
    practitioner,
    handleDateChange,
    handleFilterTypeChange,
    handlePractitionerChange,
    resetAll,
    resetPeriod,
    hasActiveFilters,
    isApplying,
  };
}
